import BaseLayer from "../tileLayers/BaseLayer";
import {getEyeCheckbox} from "../components/components";
import {Stack, Typography} from "@mui/material";
import VectorSource from "ol/source/Vector";
import {getAnnotationsFeature, isROIFeature} from "../utils/annotations_app_utils";
import {AnnotationsConstants} from "../../../utils/const";
import {Fill, Icon, Stroke, Style, Text as OlText} from "ol/style";
import {isSuperuser, notNull} from "../../../utils/utils";
import cookie from "react-cookies";
import Point from "ol/geom/Point";
import {
	annoLabelFont,
	checkAbstractAppPermission,
	getAbstractConfigValue,
	showAnnotationsLabel
} from "../../utils/abstract_app_maker";
import {tileViewApp} from "../app_maker";
import {selectedAnnotationStyle} from "../utils/map_utils";

class BaseAnnotationLayer extends BaseLayer {
	defaultVisible = true;
	markedColor = "#7CFC00";
	baseText = new OlText({
		font: 'bold 20px "Open Sans", "Helvetica", "sans-serif"',
		placement: AnnotationsConstants.LINE,
		textBaseline: 'bottom',
		fill: new Fill({
			color: AnnotationsConstants.DEFAULT_ANNO_COLOR
		}),
		backgroundFill: new Fill({
			color: "#ffffff"
		}),
		backgroundStroke: new Fill({
			color: "#ffffff"
		}),
		rotation: 0,
		overflow: false,
	});
	baseStyle = new Style({
		stroke: new Stroke({
			color: AnnotationsConstants.DEFAULT_ANNO_COLOR,
			width: 2
		}),
		fill: new Fill({
			color: 'rgba(255, 0, 0, 0.0)'
		}),
	});
	selectedStyle = selectedAnnotationStyle();
	otherStyle = new Style({
		stroke: new Stroke({
			color: "white",
			width: 2
		}),
	});
	dashedLineStyle = new Style({
		stroke: new Stroke({
			width: 2,
			color: AnnotationsConstants.DEFAULT_ANNO_COLOR,
			lineDash: [4, 6]  // [dashLength, gapLength]
		}),
		fill: new Fill({
			color: 'rgba(255, 0, 0, 0.0)'
		}),
	});

	componentDidMount() {
		this.init();
		this.layer.set("drawer", this.drawer);
		this.layer.set("tools", this.tools);
		this.layer.get("drew") ? this.setDrawCompleteState() : this.onDrawComplete();
		this.showAnnotationsLabel = checkAbstractAppPermission(showAnnotationsLabel);
		this.annoLabelFont = getAbstractConfigValue(annoLabelFont, 20);
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		if (prevProps.annotations !== this.props.annotations || 
				prevProps.selectedAnnotations !== this.props.selectedAnnotations)
			this.layer.setSource(this.getSource());
	}

	getSource = () => {
		let annotations = this.props.annotations?.filter(annotation => annotation.anno_drawer === this.drawer);
		let selectedAnnotations = this.props.selectedAnnotations;

		let features = getAnnotationsFeature(annotations);
		const existingFeatures = notNull(this.layer) ? this.layer.getSource().getFeatures() || [] : [];
		const existingFeatureIds = new Set(existingFeatures.filter(feature => feature.values_?.preserveFeature === true).map(feature => feature.getId()));

		let allFeatures = features;

		if (existingFeatureIds.size > 0) {
			const newFeatures = features?.filter(feature => !existingFeatureIds.has(feature.getId()));

			if(selectedAnnotations && features && this.selectInteraction) {
				this.selectInteraction.getFeatures().clear();
				let selectedFeatures = newFeatures?.filter(feature => selectedAnnotations.includes(feature.getId()));
				this.selectInteraction.getFeatures().extend(selectedFeatures)
			}
			allFeatures = newFeatures?.length > 0 ? [...existingFeatures, ...newFeatures] : existingFeatures;
		}

		return new VectorSource({
			features: allFeatures,
			wrapX: false,
		});
	}

	toggleVisible = () => this.setState({visible: !this.state.visible}, this.onToggleVisible);

	style = (feature) => {
		let annoStyle = this.baseStyle;
		let annotation = feature.getProperties().annotation;
		if (isSuperuser() && annotation?.creator?.toString() === cookie.loadAll().user_id) {
			annoStyle = this.dashedLineStyle;
		}
		if (notNull(annotation?.meta['width'])) {
			annoStyle.getStroke().setWidth(annotation.meta['width']);
			this.otherStyle.getStroke().setWidth(2 * annotation.meta['width'])
		}
		if (annotation?.title === 'Border' && annotation?.color === '#000') {
			annoStyle.getStroke().setWidth(1);
			this.otherStyle.getStroke().setWidth(0);
		}
		this.otherStyle.getStroke().setColor('#fff');
		annoStyle.setFill(new Fill({
			color: 'rgba(255, 0, 0, 0.0)'
		}));
		this.baseText.setOverflow(false);
		this.baseText.setFont('bold 20px "Open Sans", "Helvetica", "sans-serif"');
		this.baseText.setStroke(new Stroke({color: 'rgba(255, 255, 255, 0.0)', width: 0}));
		this.baseText.setText(feature.get('title'));
        if (feature.getProperties().marked) {
            annoStyle.getStroke().setColor(this.markedColor);
            this.baseText.getFill().setColor(this.markedColor);
        } else if (isROIFeature(feature)) {
            annoStyle.getStroke().setColor(AnnotationsConstants.ROI_COLOR);
            this.baseText.getFill().setColor(AnnotationsConstants.ROI_COLOR);
        } else if (feature.getProperties().color) {
            annoStyle.getStroke().setColor(feature.getProperties().color);
            this.baseText.getFill().setColor(feature.getProperties().color);
        }
        if (annotation?.tool_type === AnnotationsConstants.TOOL_TYPE.LABEL) {
            annoStyle.getStroke().setColor('rgba(255, 0, 0, 0.0)');
            this.baseText.setOverflow(true);
            this.baseText.setFont(`bold ${this.annoLabelFont}px "Open Sans", "Helvetica", "sans-serif"`);
            this.baseText.setStroke(new Stroke({color: 'rgba(255, 255, 255, 0.5)', width: 10}));
            this.otherStyle.getStroke().setColor('rgba(255, 0, 0, 0.0)');
        }
		this.getText(annoStyle);
        if (!this.showAnnotationsLabel && annotation?.tool_type !== AnnotationsConstants.TOOL_TYPE.LABEL) {
            annoStyle.setText('')
        }

        if (this.props.selectedAnnotations?.includes(feature.getId())) {
            if (annotation?.tool_type === AnnotationsConstants.TOOL_TYPE.LABEL) {
                this.selectedStyle[1].getStroke().setColor('rgba(255, 0, 0, 0.0)');
                this.otherStyle.getStroke().setColor('rgba(255, 0, 0, 0.0)');
            }
            this.getText(this.selectedStyle[1]);
            return [this.otherStyle, ...this.selectedStyle];
        }
        if (annotation?.title === 'Border' && annotation.color === '#000') {
            annoStyle.setText('');
            annoStyle.setFill(null);
        }
        if (annotation?.anno_drawer === tileViewApp.drawer) {
            annoStyle.setFill(null);
        }
        if (annotation?.tool_type === AnnotationsConstants.TOOL_TYPE.ARROW) {
            annoStyle.setText('');
            const geometry = feature.getGeometry();
            geometry.forEachSegment((start, end) => {
                const dx = end[0] - start[0];
                const dy = end[1] - start[1];
                const rotation = Math.atan2(dy, dx);
                const color = annotation.color;
                let arrowSVG = `
    					<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80">
      						<text x="50" y="52" font-size="35" font-weight="bold" fill="${color}">&#62;</text>
   						</svg>
				`;
				const arrowAnno = `data:image/svg+xml;utf8,${encodeURIComponent(arrowSVG)}`;

				// Arrow style
				this.arrowStyle = new Style({
					geometry: new Point(end),
					image: new Icon({
						src: arrowAnno,
						anchor: [0.75, 0.5],
						rotateWithView: true,
						rotation: -rotation,
					}),
				})
			})
			return [this.otherStyle, annoStyle, this.arrowStyle];
		}
		return [this.otherStyle, annoStyle];
	}

	getText = (annoStyle) => annoStyle.setText(this.baseText);

	render() {
		return <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
			{getEyeCheckbox(this.state.visible, undefined, this.toggleVisible)}
			<Typography variant={"caption"} color={"whitesmoke"}>{this.label}</Typography>
		</Stack>
	}
}

export default BaseAnnotationLayer;